/*
 * Instant search (native, ConvertCart-parity replacement).
 * Renders the real search results (sections/instant-search-results.liquid) into
 * a dropdown (desktop) / full-screen layer (mobile), with working facets + sort,
 * reusing product-card (so case pricing + tag icons + add-to-cart come for free).
 *
 * Markup contract (see snippets/predictive-search.liquid):
 *   <instant-search>
 *     <form class="search" action="/search"> ... <input class="js-search-input"> ... </form>
 *     <div class="instant-search__panel" data-instant-panel hidden>
 *       <div class="instant-search__suggestions" data-instant-suggestions></div>
 *       <div class="instant-search__results" data-instant-results></div>
 *     </div>
 *     <div class="overlay js-search-overlay"></div>
 *   </instant-search>
 */
/* ---------------------------------------------------------------------------
 * Typo tolerance for wine vocabulary.
 * Native Shopify search has no fuzzy matching: "primativo" returns a handful of
 * (mostly sold-out) stragglers while "primitivo" returns the full range. Wine
 * words are typo magnets (foreign varietals, regions, producers), so when a
 * query is sparse we try to correct each token against (a) a curated misspelling
 * map, (b) a dictionary of varietals/regions/wine words below, plus (c) the
 * store's own vendor names (injected as JSON by predictive-search.liquid).
 * ------------------------------------------------------------------------- */
const ISS_TYPO_MAP = {
  primativo: 'primitivo', primotivo: 'primitivo', primitvo: 'primitivo',
  shirah: 'shiraz', sirah: 'shiraz', shirras: 'shiraz', syra: 'syrah',
  savignon: 'sauvignon', savingnon: 'sauvignon', sauvingnon: 'sauvignon', sauvignion: 'sauvignon',
  reisling: 'riesling', resling: 'riesling', riesing: 'riesling', reesling: 'riesling',
  chardonay: 'chardonnay', chardonney: 'chardonnay', chardoney: 'chardonnay',
  cabinet: 'cabernet', cabernay: 'cabernet', merlo: 'merlot',
  granache: 'grenache', grenach: 'grenache', tempranilo: 'tempranillo',
  sangiovesse: 'sangiovese', sangiovase: 'sangiovese',
  // From 6 months of ConvertCart zero-result / deep-click logs (Jason 1 Aug):
  miscato: 'moscato', sangivas: 'sangiovese', sangsovese: 'sangiovese',
  sengiovese: 'sangiovese', cliquot: 'clicquot', clicquo: 'clicquot',
  nebiolo: 'nebbiolo', nebiollo: 'nebbiolo', pino: 'pinot', grigo: 'grigio',
  moscoto: 'moscato', proseco: 'prosecco', champange: 'champagne', shampagne: 'champagne',
  vermintino: 'vermentino', gewurtztraminer: 'gewurztraminer', gewertztraminer: 'gewurztraminer',
  margret: 'margaret', magaret: 'margaret', barrossa: 'barossa', barosa: 'barossa',
  coonawara: 'coonawarra', mclarren: 'mclaren', penfold: 'penfolds',
  chateuneuf: 'chateauneuf', chatauneuf: 'chateauneuf', clair: 'clare'
};
const ISS_TERMS = (
  'shiraz syrah chardonnay sauvignon blanc cabernet merlot riesling primitivo pinot noir grigio gris ' +
  'grenache tempranillo sangiovese nebbiolo barbera gewurztraminer verdelho semillon viognier malbec ' +
  'zinfandel moscato muscat prosecco champagne sparkling vermentino fiano montepulciano nero davola ' +
  'mourvedre monastrell carmenere gamay chenin albarino garnacha cinsault dolcetto lambrusco trebbiano ' +
  'verdejo godello mencia touriga marsanne roussanne gruner veltliner rose rosato sangria vermouth ' +
  'barossa mclaren vale coonawarra yarra hunter clare eden adelaide mornington heathcote rutherglen ' +
  'tasmania marlborough otago hawkes margaret river great southern langhorne padthaway wrattonbully ' +
  'burgundy bordeaux rhone chateauneuf pape rioja priorat ribera duero tuscany chianti brunello ' +
  'montalcino barolo barbaresco piedmont puglia manduria sicily etna veneto valpolicella amarone ' +
  'ripasso soave alsace loire sancerre chablis provence languedoc beaujolais mosel rheingau mendoza ' +
  'maipo stellenbosch swartland salento abruzzo campania friuli alto adige gavi ' +
  'magnum vintage reserve riserva reserva fortified tawny port sherry cognac armagnac brandy gin ' +
  'whisky whiskey vodka liqueur cleanskin organic biodynamic dessert botrytis'
).split(' ');

function issEditDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev = new Array(b.length + 1);
  const cur = new Array(b.length + 1);
  let prevPrev = null;
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (prevPrev && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cur[j] = Math.min(cur[j], prevPrev[j - 2] + 1); // transposition ("reisling")
      }
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    prevPrev = prev.slice();
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length];
}

/* Correct a whole query. Returns the corrected string, or null if nothing to fix. */
// Whole-phrase fixes the token corrector can't express — e.g. a spacing error where
// no single token is wrong. Checked before tokenizing. (Jason 1 Aug, from CC logs:
// "chateau neuf" was a top zero-result term for six months.)
const ISS_PHRASE_MAP = {
  'chateau neuf': 'chateauneuf du pape',
  'chateau neuf du pape': 'chateauneuf du pape',
  'chateauneuf du pap': 'chateauneuf du pape',
  'veuve cliquot': 'veuve clicquot',
  'veuve clicquo': 'veuve clicquot'
};

function issCorrectTerm(term, extraTerms) {
  const phrase = ISS_PHRASE_MAP[term.toLowerCase().trim().replace(/\s+/g, ' ')];
  if (phrase && phrase !== term.toLowerCase()) return phrase;
  const dict = extraTerms && extraTerms.length ? ISS_TERMS.concat(extraTerms) : ISS_TERMS;
  const dictSet = new Set(dict);
  let changed = 0;
  const out = term.toLowerCase().split(/\s+/).map((token) => {
    if (token.length < 4 || changed >= 2) return token;
    if (ISS_TYPO_MAP[token]) { changed++; return ISS_TYPO_MAP[token]; }
    if (dictSet.has(token)) return token;
    const max = token.length <= 6 ? 1 : 2;
    let best = null;
    let bestD = max + 1;
    for (const cand of dict) {
      const d = issEditDistance(token, cand, max);
      if (d < bestD) { bestD = d; best = cand; if (d === 1 && token.length > 6) break; }
    }
    if (best && bestD <= max) { changed++; return best; }
    return token;
  }).join(' ');
  return changed && out !== term.toLowerCase() ? out : null;
}
window.issCorrectTerm = issCorrectTerm; // reused by main-search "did you mean"

if (!customElements.get('instant-search')) {
  class InstantSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.form = this.input ? this.input.closest('form') : this.querySelector('form');
      this.panel = this.querySelector('[data-instant-panel]');
      this.resultsEl = this.querySelector('[data-instant-results]');
      this.suggestionsEl = this.querySelector('[data-instant-suggestions]');
      this.overlay = this.querySelector('.js-search-overlay');
      this.section = this.getAttribute('data-section') || 'instant-search-results';
      this.minChars = parseInt(this.getAttribute('data-min-chars'), 10) || 2;
      this.searchBase = (this.form && this.form.getAttribute('action')) || (window.theme && theme.routes && theme.routes.searchUrl) || '/search';
      this.currentUrl = null;       // last results URL fetched (no section_id) — used for View All / sort
      // Search-analytics journey (Jason 1 Aug): ONE ww_search event per search journey,
      // fired at the terminal action (result click / view-all / submit / abandon) with
      // whatever the query was at that moment — so "cha" is logged if the shopper
      // genuinely stopped and clicked there, but cha->chard->chardonnay is not three rows.
      this._j = { q: '', count: 0, corrected: false, logged: false, zeroTimer: null, zeroFor: '', backoffTo: null };
      this.debounceTimer = null;
      this.controller = null;       // aborts the in-flight results (section) fetch
      this.suggestController = null;// aborts the in-flight suggestions fetch
      this._reqId = 0;              // monotonic id so only the latest request injects/opens
      this.fallbackThreshold = 5;   // raw results below this => use the best query suggestion (partial-word match)
      this._completion = null;      // { from, to } when a partial term was completed, so submit matches the dropdown
      // Store vendor names (predictive-search.liquid injects them) extend the typo dictionary,
      // so misspelled producer names ("penfols", "oatly") correct against the real catalogue.
      this.extraTerms = [];
      const vendorsEl = document.querySelector('[data-iss-vendors]');
      if (vendorsEl) {
        try {
          this.extraTerms = Array.from(new Set(
            JSON.parse(vendorsEl.textContent)
              .flatMap((v) => String(v).toLowerCase().split(/[^a-z0-9]+/))
              .filter((w) => w.length >= 4)
          ));
        } catch (e) { /* vendor list is an enhancement only */ }
      }
    }

    connectedCallback() {
      if (!this.input || !this.panel) return;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', () => { if (this.resultsEl.innerHTML.trim()) this.open(); });
      // Submitting (Enter / search button) on a partial term should search the COMPLETED term,
      // so the results page matches the products shown in the dropdown (not the raw partial).
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Delegated interactions inside the injected results.
      this.resultsEl.addEventListener('click', this.onResultsClick.bind(this));
      this.resultsEl.addEventListener('change', this.onResultsChange.bind(this));
      if (this.suggestionsEl) this.suggestionsEl.addEventListener('click', this.onSuggestionClick.bind(this));

      // Mobile filter/sort bar (static chrome) lives in the panel.
      /* Mobile: the keyboard stayed up while browsing results, and the only way to get
         rid of it was to tap — which landed on a product card and navigated away. Drop
         the keyboard as soon as the shopper starts scrolling the panel, so browsing
         costs them nothing (Jason 25 Jul). passive: the listeners must not slow the
         scroll; capture on 'scroll' because scroll events don't bubble. */
      const dismissKeyboard = (e) => {
        const t = e.target;
        if (t && t.closest && t.closest('.js-search-input')) return; // still typing
        if (document.activeElement === this.input) this.input.blur();
      };
      this.panel.addEventListener('touchmove', dismissKeyboard, { passive: true });
      this.panel.addEventListener('scroll', dismissKeyboard, { passive: true, capture: true });

      this.panel.addEventListener('click', this.onPanelChrome.bind(this));

      /* Tapping away from an OPEN sort/filter sheet should only close that sheet. There
         was no outside-click dismissal at all, so the tap fell through to whatever was
         behind it (a product card, a facet). Capture phase runs before the target's own
         handlers, so the tap is swallowed (Jason 25 Jul). */
      this.panel.addEventListener('click', (e) => {
        let openSheet = null;
        if (this.panel.classList.contains('iss--show-sort')) {
          openSheet = this.panel.querySelector('[data-iss-sortsheet]');
        } else if (this.panel.classList.contains('iss--show-facets')) {
          openSheet = this.panel.querySelector('.iss__facets');
        }
        if (!openSheet || openSheet.contains(e.target)) return;
        // Let the bar's own toggles through so they can still close/switch sheets.
        if (e.target.closest('[data-iss-filter], [data-iss-sort]')) return;
        e.preventDefault();
        e.stopPropagation();
        this.panel.classList.remove('iss--show-facets', 'iss--show-sort');
      }, true);

      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      const reset = this.querySelector('.js-search-reset');
      if (reset) reset.addEventListener('click', () => { this.clear(); });

      // Close the dropdown when a drawer/modal opens — e.g. clicking add-to-cart / "Choose
      // options" on a result opens the quick-add (or cart) drawer; without this the dropdown
      // stays open behind it and overlaps. The theme adds body.fixed when any drawer opens.
      this.bodyObserver = new MutationObserver(() => {
        if (this.hasAttribute('open') && document.body.classList.contains('fixed')) this.close();
      });
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      // Close on any click outside the dropdown/input. Robust regardless of the theme's base
      // .overlay { pointer-events:none }: a click on the (transparent) overlay OR on page content
      // behind it both resolve to "outside" here.
      this._onDocClick = (e) => {
        if (!this.hasAttribute('open')) return;
        const onOverlay = this.overlay && this.overlay.contains(e.target);
        if (this.contains(e.target) && !onOverlay) return;
        // A click-away should ONLY dismiss the panel — previously it also activated
        // whatever sat underneath (product card, link, quick-add). Capture phase +
        // these two calls stop it reaching the target at all (Jason 25 Jul).
        e.preventDefault();
        e.stopPropagation();
        this.close();
      };
      document.addEventListener('click', this._onDocClick, true);
    }

    onInput() {
      const term = this.input.value.trim();
      clearTimeout(this.debounceTimer);
      if (term.length < this.minChars) {
        this._reqId++;                                  // cancel any in-flight request
        if (this.controller) this.controller.abort();
        this.close();
        this.resultsEl.innerHTML = '';
        return;
      }
      this.debounceTimer = setTimeout(() => this.typeSearch(term), 250);
    }

    /* Typing path. Native full-text search matches whole words well but partial words
       poorly (e.g. "rober" -> 1 result, "oatle" -> 4). When the raw term is sparse we
       re-run with the best query suggestion ("robert oatley" -> 33), which matches AND
       still returns facets — so the filter sidebar stays correct. */
    async typeSearch(term) {
      const reqId = ++this._reqId;
      const suggestP = this.fetchSuggestions(term);     // left column + best completion
      const r1 = await this.fetchSectionText(this.buildSearchUrl(term));
      if (!r1 || reqId !== this._reqId) { suggestP.catch(() => {}); return; }
      // Fuzzy-wall guard (Jason 27 Aug): plenty of results but few/none whose TITLE
      // carries a query token means the engine typo-matched the whole phrase
      // ("veuve clicquot" -> a Vouvray wall with the two stocked Veuves buried).
      // The stocked token's own results are strictly better — re-run on it, banner
      // it, and let submit / View All follow it. Mirrors ww-search-backoff on the
      // results page; sparse pages (below threshold) keep the typo/suggestion ladder.
      if (r1.count >= this.fallbackThreshold) {
        const handled = await this.tokenBackoff(term, r1, reqId);
        if (handled || reqId !== this._reqId) { suggestP.catch(() => {}); return; }
      }
      if (r1.count < this.fallbackThreshold) {
        // Typo correction FIRST: it's local, and a corrected word ("primativo" ->
        // "primitivo", 45 results) beats a prefix completion derived from the typo
        // ("primitivo collantes", 4 results). Suggestions can't fix mid-word typos.
        const corrected = issCorrectTerm(term, this.extraTerms);
        if (corrected && corrected !== term.toLowerCase()) {
          const cUrl = this.buildSearchUrl(corrected);
          const r3 = await this.fetchSectionText(cUrl);
          if (!r3 || reqId !== this._reqId) return;
          if (r3.count >= this.fallbackThreshold && r3.count > r1.count) {
            this._completion = { from: term, to: corrected }; // submit / View All follow the corrected term
            this.injectText(cUrl, r3.sectionHTML);
            this.journeyUpdate(r3.count, true);
            this.showTypoNotice(corrected, term);
            return;
          }
        }
        const top = await suggestP;
        if (reqId !== this._reqId) return;
        if (top && top.toLowerCase() !== term.toLowerCase()) {
          const topUrl = this.buildSearchUrl(top);
          const r2 = await this.fetchSectionText(topUrl);
          if (!r2 || reqId !== this._reqId) return;
          if (r2.count > r1.count) {
            this._completion = { from: term, to: top };   // remember for submit / View All matching
            this.injectText(topUrl, r2.sectionHTML);
            this.journeyUpdate(r2.count, false);
            return;
          }
        }
      }
      this._completion = null;
      this.injectText(this.buildSearchUrl(term), r1.sectionHTML);
      this.journeyUpdate(r1.count, false);
      suggestP.catch(() => {});
    }

    /* "Showing results for X - search instead for Y" banner above corrected results. */
    showTypoNotice(to, from) {
      const note = document.createElement('div');
      note.className = 'iss-typo-note';
      note.innerHTML = `${this.escape('Showing results for')} <strong>${this.escape(to)}</strong>` +
        `<button type="button" class="iss-typo-note__raw" data-raw-term="${this.escape(from)}">` +
        `${this.escape('Search instead for')} “${this.escape(from)}”</button>`;
      const bar = this.resultsEl.querySelector('.iss__bar');
      if (bar) bar.insertAdjacentElement('afterend', note);
      else this.resultsEl.prepend(note);
    }

    buildSearchUrl(term) {
      return `${this.searchBase}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last`;
    }

    /* --- Token backoff (shared doctrine with snippets/ww-search-backoff.liquid) --- */

    issQueryTokens(term) {
      const generic = ['wine', 'wines', 'the', 'and', 'red', 'white', 'rose', 'pack', 'case', 'bottle'];
      return term.toLowerCase().split(/\s+/)
        .filter((t) => t.length >= 3 && !/^\d+$/.test(t) && generic.indexOf(t) === -1);
    }

    /* How many rendered cards' titles carry a query token, and which token carries most. */
    issCoverage(sectionHTML, tokens) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = sectionHTML;
      const titles = Array.from(tmpl.content.querySelectorAll('.card__title')).map((el) => (el.textContent || '').toLowerCase());
      const perToken = {};
      let matched = 0;
      titles.forEach((t) => {
        let hit = false;
        tokens.forEach((tok) => {
          if (t.indexOf(tok) !== -1) { perToken[tok] = (perToken[tok] || 0) + 1; hit = true; }
        });
        if (hit) matched++;
      });
      let bestToken = null;
      Object.keys(perToken).forEach((k) => { if (!bestToken || perToken[k] > perToken[bestToken]) bestToken = k; });
      return { total: titles.length, matched, bestToken };
    }

    /* No token on the rendered page at all: ask suggest.json which token we stock. */
    async probeTokens(tokens) {
      const base = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
      const scores = await Promise.all(tokens.map((tok) =>
        fetch(`${base}.json?q=${encodeURIComponent(tok)}&resources%5Btype%5D=product&resources%5Blimit%5D=5`)
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            const products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
            return { token: tok, hits: products.filter((p) => (p.title || '').toLowerCase().indexOf(tok) !== -1).length };
          })
          .catch(() => ({ token: tok, hits: 0 }))
      ));
      let best = null;
      scores.forEach((s) => {
        if (s.hits > 0 && (!best || s.hits > best.hits || (s.hits === best.hits && s.token.length > best.token.length))) best = s;
      });
      return best ? best.token : null;
    }

    /* Returns true when it rendered a token-backoff result set instead of r1. */
    async tokenBackoff(term, r1, reqId) {
      try {
        const tokens = this.issQueryTokens(term);
        if (tokens.length < 2 && !(tokens.length === 1 && /\s/.test(term.trim()))) return false;
        const cov = this.issCoverage(r1.sectionHTML, tokens);
        if (!cov.total) return false;
        const minority = cov.matched <= 3 && cov.matched / cov.total <= 0.25;
        if (!minority) return false;
        let target = cov.bestToken;
        if (!target) target = await this.probeTokens(tokens.slice(0, 4));
        if (!target || reqId !== this._reqId) return false;
        const tUrl = this.buildSearchUrl(target);
        const rT = await this.fetchSectionText(tUrl);
        if (!rT || reqId !== this._reqId) return false;
        // Worth swapping only if the token set is bigger, or the raw page had zero
        // genuine matches and the token page has real ones.
        if (rT.count <= r1.count && !(cov.matched === 0 && rT.count > 0)) return false;
        this._completion = { from: term, to: target }; // submit / View All follow the token
        this.injectText(tUrl, rT.sectionHTML);
        this.journeyUpdate(rT.count, true);
        this._j.backoffTo = target;
        this.showTypoNotice(target, term);
        return true;
      } catch (e) { return false; } // any failure: fall back to the raw render
    }

    /* Full results-page URL (mirrors a native submit: keeps the form's `type`, adds prefix). */
    buildPageUrl(term) {
      const typeEl = this.form && this.form.querySelector('[name="type"]');
      const type = typeEl && typeEl.value ? typeEl.value : 'product';
      return `${this.searchBase}?q=${encodeURIComponent(term)}&type=${encodeURIComponent(type)}&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last`;
    }

    onSubmit(e) {
      this.journeyEnd('submit');
      const term = this.input.value.trim();
      // If this exact term was completed during typing (e.g. "rober" -> "robert oatley"),
      // navigate to the completed search so the page matches the instant dropdown.
      if (this._completion && this._completion.from === term && this._completion.to
          && this._completion.to.toLowerCase() !== term.toLowerCase()) {
        e.preventDefault();
        let dest = this.buildPageUrl(this._completion.to);
        // Token-backoff completions carry the original phrase so the results page
        // shows its "closest matches" banner (ww-search-backoff landing side).
        if (this._j.backoffTo === this._completion.to) {
          dest += `&backoff_from=${encodeURIComponent(term)}`;
        }
        window.location.assign(dest);
      }
    }

    /* Discrete action (facet / sort / price / suggestion click): fetch + inject one URL. */
    async fetchResults(url) {
      const reqId = ++this._reqId;
      const r = await this.fetchSectionText(url);
      if (!r || reqId !== this._reqId) return null;
      this.injectText(url, r.sectionHTML);
      this.journeyUpdate(r.count, false);
      return r.count;
    }

    /* Fetch a results URL via the Section Rendering API. Returns { sectionHTML, count }
       (count = search.results_count) or null if the request was aborted/failed. Does NOT
       touch the DOM, so typeSearch can read the count and decide whether to inject. */
    async fetchSectionText(url) {
      this.currentUrl = url;
      this.setLoading(true);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const sep = url.includes('?') ? '&' : '?';
      try {
        const res = await fetch(`${url}${sep}section_id=${this.section}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        const sectionEl = tmpl.content.querySelector(`#shopify-section-${this.section}`) || tmpl.content.querySelector('.iss');
        const issEl = sectionEl && sectionEl.matches('.iss') ? sectionEl : (sectionEl ? sectionEl.querySelector('.iss') : null);
        const count = issEl ? (parseInt(issEl.getAttribute('data-count'), 10) || 0) : 0;
        return { sectionHTML: sectionEl ? sectionEl.innerHTML : text, count };
      } catch (e) {
        if (e.name !== 'AbortError') { this.resultsEl.innerHTML = ''; }
        return null;
      } finally {
        this.setLoading(false);
      }
    }

    /* Inject already-fetched section HTML into the results column and post-process it. */
    injectText(url, sectionHTML) {
      this.currentUrl = url;
      // The section renders filter groups collapsed by default; preserve any the shopper expanded
      // so a facet/sort/price re-fetch doesn't collapse the group they're working in.
      const openKeys = Array.from(this.resultsEl.querySelectorAll('.iss-facet[open]')).map((d) => d.getAttribute('data-facet-key')).filter(Boolean);
      this.resultsEl.innerHTML = sectionHTML;
      // product-card injects a per-card icon-legend modal (.modal/#legendsPopup, position:fixed,
      // full-viewport). In the dropdown context the theme's hide rule doesn't apply, so it can
      // cover the whole screen. We don't want the legend popup in instant search — strip the
      // modals and disarm the icon openers (clicking an icon in the dropdown just does nothing).
      this.resultsEl.querySelectorAll('modal-dialog, .modal, [id="legendsPopup"]').forEach((m) => m.remove());
      this.resultsEl.querySelectorAll('modal-opener').forEach((o) => o.removeAttribute('data-modal'));
      // product-card images are lazy-loaded (data-src/data-srcset); the theme's lazyload
      // observer doesn't watch injected nodes, so they stay blank. Promote them to load now.
      this.resultsEl.querySelectorAll('img[data-src], source[data-srcset], img[data-srcset]').forEach((el) => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
        el.classList.remove('no-js-hidden', 'lazyload');
      });
      openKeys.forEach((k) => {
        const d = this.resultsEl.querySelector(`.iss-facet[data-facet-key="${k.replace(/["\\]/g, '\\$&')}"]`);
        if (d) d.open = true;
      });
      this.syncViewAll();
      this.open();
    }

    /* Query suggestions (left column) via the Predictive Search API.
       Returns the top suggestion text (used by typeSearch to complete a partial word), or null. */
    async fetchSuggestions(term) {
      if (!this.suggestionsEl) return null;
      try {
        if (this.suggestController) this.suggestController.abort();
        this.suggestController = new AbortController();
        const base = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
        const res = await fetch(`${base}.json?q=${encodeURIComponent(term)}&resources%5Btype%5D=query&resources%5Blimit%5D=6`, { signal: this.suggestController.signal });
        if (!res.ok) return null;
        const data = await res.json();
        const queries = (data.resources && data.resources.results && data.resources.results.queries) || [];
        let html = `<h4 class="instant-search__col-title">Suggestions</h4><ul class="instant-search__suggest-list" role="list">`;
        if (!queries.length) {
          html += `<li><button type="button" class="instant-search__suggest" data-term="${this.escape(term)}">${this.escape(term)}</button></li>`;
        } else {
          queries.forEach((q) => {
            const t = q.text || term;
            html += `<li><button type="button" class="instant-search__suggest" data-term="${this.escape(t)}">${q.styled_text || this.escape(t)}</button></li>`;
          });
        }
        html += `</ul>`;
        this.suggestionsEl.innerHTML = html;
        return queries.length ? (queries[0].text || null) : null;
      } catch (e) { return null; }
    }

    onSuggestionClick(e) {
      const btn = e.target.closest('.instant-search__suggest');
      if (!btn) return;
      e.preventDefault();
      const term = btn.getAttribute('data-term');
      this.input.value = term;
      this.fetchResults(this.buildSearchUrl(term));
    }

    /* dataLayer emit — additive only, never touches existing tracking; a throw here
       must never break search. GTM turns these into GA4 events (Jason's tag list). */
    track(name, params) {
      try {
        const payload = Object.assign({ event: name, search_surface: 'instant' }, params || {});
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        // Shopify pixel bridge: GTM turned out not to be installed anywhere, so the
        // real consumer is the "WW Search Analytics" custom pixel (Customer events),
        // which only sees events published through this API (Jason 2 Aug).
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish(name, payload);
        }
      } catch (e) { /* analytics must never break search */ }
    }

    /* Called after every successful results render. */
    journeyUpdate(count, corrected) {
      try {
        const q = this.input.value.trim().toLowerCase();
        if (q !== this._j.q) { this._j.logged = false; this._j.backoffTo = null; }
        this._j.q = q;
        this._j.count = count;
        this._j.corrected = !!corrected;
        clearTimeout(this._j.zeroTimer);
        // Zero-result moments are logged per query even mid-journey — they're the
        // synonym-mining signal ("reisling" sat at zero for six months in CC's logs).
        if (count === 0 && q && this._j.zeroFor !== q) {
          this._j.zeroTimer = setTimeout(() => {
            this._j.zeroFor = q;
            this.track('ww_search_zero', { search_term: q });
          }, 1500);
        }
      } catch (e) { /* never break search */ }
    }

    /* Terminal action: log the journey once. */
    journeyEnd(action, extra) {
      try {
        if (!this._j.q || this._j.logged) return;
        this._j.logged = true;
        clearTimeout(this._j.zeroTimer);
        this.track('ww_search', Object.assign({
          search_term: this._j.q,
          results_count: this._j.count,
          zero_results: this._j.count === 0,
          corrected: this._j.corrected,
          backoff_to: this._j.backoffTo || null,
          action
        }, extra || {}));
      } catch (e) { /* never break search */ }
    }

    /* Position = 1-based index of the clicked product among rendered results. */
    clickedPosition(link) {
      try {
        const handleOf = (a) => ((a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/) || [])[1];
        const clicked = handleOf(link);
        if (!clicked) return null;
        const seen = [];
        this.resultsEl.querySelectorAll('a[href*="/products/"]').forEach((a) => {
          const h = handleOf(a);
          if (h && seen.indexOf(h) === -1) seen.push(h);
        });
        const i = seen.indexOf(clicked);
        return i === -1 ? null : { position: i + 1, handle: clicked };
      } catch (e) { return null; }
    }

    onResultsClick(e) {
      // Analytics first, never preventDefault for it: product links and quick-add are
      // both terminal actions for the search journey.
      try {
        const prodLink = e.target.closest('a[href*="/products/"]');
        if (prodLink && this.resultsEl.contains(prodLink)) {
          const pos = this.clickedPosition(prodLink);
          if (pos) this.track('ww_search_click', { search_term: this._j.q, position: pos.position, product_handle: pos.handle });
          this.journeyEnd('click');
        }
        if (e.target.closest('.js-quick-add')) {
          this.track('ww_search_click', { search_term: this._j.q, quick_add: true });
          this.journeyEnd('quick_add');
        }
        if (e.target.closest('[data-viewall]')) this.journeyEnd('view_all');
      } catch (err) { /* never break search */ }

      // Adding to cart from a result opens the quick-add / cart drawer — close the dropdown so it
      // doesn't overlap. Don't preventDefault: let the theme's quick-add handler run.
      if (e.target.closest('.js-quick-add')) { setTimeout(() => this.close(), 0); return; }

      // "Search instead for 'primativo'" on the typo banner: run the raw query verbatim.
      const raw = e.target.closest('[data-raw-term]');
      if (raw) {
        e.preventDefault();
        this._completion = null;
        this.fetchResults(this.buildSearchUrl(raw.getAttribute('data-raw-term')));
        return;
      }

      const facet = e.target.closest('[data-facet-link]');
      if (facet) {
        e.preventDefault();
        const g = facet.closest('[data-facet]');
        this.track('ww_search_filter', { search_term: this._j.q, facet_key: (g && g.getAttribute('data-facet-key')) || 'unknown' });
        this.fetchResults(facet.getAttribute('href'));
        return;
      }

      const more = e.target.closest('[data-facet-more]');
      if (more) {
        e.preventDefault();
        const group = more.closest('[data-facet]');
        group.querySelectorAll('.iss-facet__item--extra').forEach((li) => li.classList.remove('is-hidden'));
        more.remove();
        return;
      }

      const priceGo = e.target.closest('[data-price-go]');
      if (priceGo) {
        e.preventDefault();
        this.applyPrice(priceGo.closest('[data-price-facet]'));
        return;
      }

      const sortOpt = e.target.closest('[data-sort-value]');
      if (sortOpt) {
        this.track('ww_search_sort', { search_term: this._j.q, sort_value: sortOpt.getAttribute('data-sort-value') });
        e.preventDefault();
        this.panel.classList.remove('iss--show-sort');
        const url = this.setParam(this.currentUrl || `${this.searchBase}?q=${encodeURIComponent(this.input.value.trim())}`, 'sort_by', sortOpt.getAttribute('data-sort-value'));
        this.fetchResults(url);
        return;
      }
      // [data-viewall] is a normal link (href kept in sync) — allow default navigation.
    }

    onResultsChange(e) {
      const sortSel = e.target.closest('[data-sort-select]');
      if (!sortSel) return;
      this.track('ww_search_sort', { search_term: this._j.q, sort_value: sortSel.value });
      const url = this.setParam(this.currentUrl || `${this.searchBase}?q=${encodeURIComponent(this.input.value.trim())}`, 'sort_by', sortSel.value);
      this.fetchResults(url);
    }

    applyPrice(wrap) {
      if (!wrap) return;
      this.track('ww_search_filter', { search_term: this._j.q, facet_key: 'price' });
      const tpl = wrap.getAttribute('data-url-template') || this.currentUrl;
      const minEl = wrap.querySelector('[data-price-min]');
      const maxEl = wrap.querySelector('[data-price-max]');
      let url = tpl;
      if (minEl) url = minEl.value ? this.setParam(url, minEl.name, minEl.value) : this.removeParam(url, minEl.name);
      if (maxEl) url = maxEl.value ? this.setParam(url, maxEl.name, maxEl.value) : this.removeParam(url, maxEl.name);
      this.fetchResults(url);
    }

    onPanelChrome(e) {
      const filterBtn = e.target.closest('[data-iss-filter]');
      if (filterBtn) { this.panel.classList.toggle('iss--show-facets'); return; }
      const sortBtn = e.target.closest('[data-iss-sort]');
      if (sortBtn) { this.panel.classList.toggle('iss--show-sort'); return; }
      const sheetClose = e.target.closest('[data-iss-sheet-close]');
      if (sheetClose) { this.panel.classList.remove('iss--show-facets', 'iss--show-sort'); return; }
      const closeBtn = e.target.closest('[data-iss-close]');
      if (closeBtn) { this.close(); return; }
    }

    syncViewAll() {
      const va = this.resultsEl.querySelector('[data-viewall]');
      if (va && this.currentUrl) va.setAttribute('href', this.currentUrl);
    }

    setParam(url, key, val) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set(key, val);
      return u.pathname + u.search;
    }
    removeParam(url, key) {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete(key);
      return u.pathname + u.search;
    }
    escape(s) { return (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    setLoading(on) { this.panel.classList.toggle('is-loading', !!on); }

    open() {
      this.panel.hidden = false;
      this.setAttribute('open', '');
      document.body.classList.add('overlay-instant-search');
      this.input.setAttribute('aria-expanded', 'true');
      // On mobile the panel is full-screen below the search input — anchor its top to the input.
      if (window.matchMedia('(max-width: 767.98px)').matches) {
        // Anchor to the search BAR, not the input: the input's bottom edge sits inside
        // the bar's 3px green border, so the panel was painting over that border and it
        // looked cut off (Jason 25 Jul).
        const anchor = this.input.closest('.search') || this.input;
        const r = anchor.getBoundingClientRect();
        this.panel.style.top = Math.max(0, Math.round(r.bottom)) + 'px';
      } else {
        this.panel.style.top = '';
      }
    }
    close() {
      this.journeyEnd('abandon');
      this._reqId++;                         // invalidate any in-flight request so it can't re-open the panel
      if (this.controller) this.controller.abort();
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.panel.classList.remove('iss--show-facets', 'iss--show-sort');
      document.body.classList.remove('overlay-instant-search');
      this.input.setAttribute('aria-expanded', 'false');
    }
    clear() { this._reqId++; if (this.controller) this.controller.abort(); this.input.value = ''; this.resultsEl.innerHTML = ''; if (this.suggestionsEl) this.suggestionsEl.innerHTML = ''; this.close(); }
  }

  customElements.define('instant-search', InstantSearch);
}
